import React, { useEffect, useState } from 'react';
import { formatShortDate, formatShortDateTime } from '../utils/dates';
import { useTranslation } from 'react-i18next';
import {
  Check, ChevronDown, CircleAlert, History, Info, Languages, Paperclip, Pencil, Reply, Send, Sparkles, Truck, UserRound,
} from 'lucide-react';
import './CarriersConversation.css';
import axios from 'axios';
import ManualConversation from './ManualConversation';
import LinkifiedText from './LinkifiedText';
import { getCarrierFailureTexts } from '../utils/carrierFailureDiagnosis';
import { getStatusLabel } from '../constants/ticketStatuses';

/**
 * Fields of a FedEx claim for review: the stored claim_payload while it waits
 * for approval, advanced_context.request_payload once sent. Lost and damaged
 * claims alike (the bank block never reaches the browser).
 */
const FEDEX_DETAIL_KEYS = {
  lost_details: ['packages_sent_count', 'packages_missing_count', 'packaging_description', 'packaging_markings',
    'missing_items_description', 'part_serial_numbers', 'electronics_serial_imei', 'order_numbers',
    'manufacturer_brand_name', 'shipment_value', 'currency', 'your_message'],
  damage_details: ['total_packages_delivered', 'damaged_packages_count', 'outer_carton_damage_description',
    'inner_packaging_description', 'content_damage_description', 'damaged_items_count', 'declared_value_goods',
    'currency', 'content_damage_same_side_as_outer', 'your_message'],
};

export const extractFedExParams = (conversation) => {
  const payload = conversation?.claim_payload || conversation?.advanced_context?.request_payload;
  if (!payload) return {};
  const params = {};
  const claimType = payload.claim_type || conversation.claim_type || conversation.advanced_context?.claim_type;
  if (claimType) params.claim_type = claimType;
  const formData = payload.lost_claim_form_data || payload.damaged_claim_form_data || {};
  const contact = formData.contact_info || {};
  ['first_name', 'last_name', 'email', 'phone_number', 'company_name', 'address_line1', 'address_line2', 'city',
    'postal_code'].forEach((key) => {
    if (contact[key]) params[key] = contact[key];
  });
  if (Array.isArray(formData.tracking_info?.tracking_numbers)) {
    params.tracking_numbers = formData.tracking_info.tracking_numbers.join(', ');
  }
  Object.entries(FEDEX_DETAIL_KEYS).forEach(([block, keys]) => {
    const details = formData[block] || {};
    keys.forEach((key) => {
      if (details[key] !== undefined && details[key] !== null && String(details[key]).trim() !== '') {
        params[key] = details[key];
      }
    });
  });
  return params;
};

const FEDEX_PENDING_HEADER = /^\s*FedEx \w+ claim pending human approval\.?\s*$/i;

// Runtime/credential keys that must never appear in the edit form or the
// params summary (mirrors backend INTERNAL_PARAM_KEYS; credentials are also
// stripped server-side).
const INTERNAL_PARAM_KEYS = new Set([
  'headless', 'test_mode', 'debug_run_id', 'files_urls', 'file_path',
  'file_name', 'upload_failures', 'otp_context', 'fallback_phone',
  'username', 'password', 'user'
]);

const RECEIVED_DIRECTIONS = ['received', 'final_response'];
// KI-205: an operator's own message to the carrier (backend REPLY_MAX_CHARS).
const REPLY_MAX_CHARS = 5000;
const OPERATOR_REPLY_ORIGIN = 'operator_reply';

// Conversation status → badge tone (orange = waits for the team, blue = in
// progress, green = done, red = failed; anything else neutral).
const STATUS_TONES = {
  pending: 'warning',
  pending_validation: 'warning',
  processing: 'info',
  sent: 'info',
  waiting_carrier_response: 'info',
  received: 'info',
  completed: 'success',
  failed: 'danger',
};

// « néerlandais » for 'nl' in a French interface; the code itself when the
// browser has no name for it.
const languageName = (code, uiLanguage) => {
  if (!code) return '';
  try {
    return new Intl.DisplayNames([uiLanguage], { type: 'language' }).of(code) || code;
  } catch (e) {
    return code;
  }
};

/**
 * The carrier conversation of a ticket (« Transporteur » view of the ticket
 * window): the request and its parameters, the exchange (latest first, the
 * rest behind « Afficher l'historique »), the decision on a request awaiting
 * validation, and the operator's own reply. Without a conversation, the
 * manual form to contact a carrier.
 */
const CarriersConversation = ({ conversation, onValidateMessage, isValidating, onRefresh, ticketFiles, ticket }) => {
  const { t, i18n } = useTranslation();
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedParams, setEditedParams] = useState({});
  const [editedMessage, setEditedMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  // A request waiting for approval is reviewed: its parameters start open.
  const [showParams, setShowParams] = useState(['pending', 'pending_validation'].includes(conversation?.status));
  const [paramSchema, setParamSchema] = useState(null);
  // Carrier replies in the reader's interface language (cached server-side),
  // by message index; the original stays one click away.
  const [translations, setTranslations] = useState({});
  const [translationState, setTranslationState] = useState('idle');
  const [showOriginal, setShowOriginal] = useState({});
  // KI-205: the operator answers the carrier on this conversation.
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [replyNotice, setReplyNotice] = useState('');

  const uiLanguage = (i18n.resolvedLanguage || i18n.language || 'fr').split('-')[0];
  const conversationId = conversation?._id;
  const receivedCount = (conversation?.messages || [])
    .filter((m) => RECEIVED_DIRECTIONS.includes(m?.direction)).length;

  useEffect(() => {
    if (!conversationId || receivedCount === 0) {
      setTranslations({});
      return undefined;
    }
    let cancelled = false;
    setTranslationState('loading');
    const token = localStorage.getItem('access_token');
    axios.post(
      `/api/tickets/carriers_conversation/${conversationId}/translations`,
      { language: uiLanguage },
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((response) => {
        if (cancelled) return;
        const byIndex = {};
        (response.data?.translations || []).forEach((item) => { byIndex[item.index] = item; });
        setTranslations(byIndex);
        setTranslationState('done');
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to translate the carrier messages:', error);
        setTranslationState('failed');
      });
    return () => { cancelled = true; };
  }, [conversationId, receivedCount, uiLanguage]);

  // When no conversation exists, show the manual form.
  if (!conversation) {
    return <ManualConversation onRefresh={onRefresh} ticketFiles={ticketFiles} ticket={ticket} />;
  }

  // Backend timestamps are naive UTC strings: never `new Date(x)` directly (KI-110).
  const formatTimestamp = (timestamp) => formatShortDateTime(timestamp);

  const editableStatuses = ['pending_validation', 'pending'];
  const contactError = conversation.error || null;

  // If there are no messages yet but the conversation is pending validation,
  // create a default message from the interaction_params.
  const messages =
    conversation.messages ||
    (conversation.status === 'pending_validation'
      ? [
          {
            direction: 'sent',
            content:
              conversation.interaction_params?.message ||
              conversation.interaction_params?.comment ||
              '',
            timestamp: conversation.created_at,
            status: 'pending'
          }
        ]
      : []);

  const handleEditClick = async (index) => {
    setEditingIndex(index);
    setSaveError('');
    setEditedMessage(messages[index]?.content || '');
    // FedEx claims: the edited message is « Votre message » on the FedEx
    // form (200 characters max); a draft from before 2026-10-09 only showed
    // a header, so start from the claim's own message.
    if (conversation.carrier_name === 'FedEx' && (conversation.claim_payload || conversation.advanced_context)) {
      const params = extractFedExParams(conversation);
      setEditedParams(params);
      const current = messages[index]?.content || '';
      if (FEDEX_PENDING_HEADER.test(current) && params.your_message) {
        setEditedMessage(params.your_message);
      }
      return;
    }
    const storedParams = {};
    Object.entries(conversation.interaction_params || {}).forEach(([key, value]) => {
      if (!INTERNAL_PARAM_KEYS.has(key)) storedParams[key] = value;
    });
    // The schema lists every field the carrier's automation can consume, so
    // a portal-required value the AI missed (e.g. Colissimo's parcel value)
    // can be FILLED here, not just corrected.
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `/api/logistics/carrier-conversations/${conversation._id}/param-schema`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setParamSchema(response.data);
      const merged = {};
      (response.data.fields || []).forEach((field) => {
        merged[field.key] = storedParams[field.key] ?? '';
      });
      Object.entries(storedParams).forEach(([key, value]) => {
        if (!(key in merged)) merged[key] = value;
      });
      setEditedParams(merged);
    } catch (error) {
      console.error('Failed to fetch carrier param schema:', error);
      setParamSchema(null);
      setEditedParams(storedParams);
    }
  };

  const handleSaveEdit = async () => {
    setSaveError('');
    try {
      const token = localStorage.getItem('access_token');
      const payload = { content: editedMessage };
      // FedEx params are derived from advanced_context for display only and
      // must never overwrite the stored interaction_params.
      if (conversation.carrier_name !== 'FedEx') {
        // Schema fields left empty AND never stored are omitted (not saved as
        // empty strings); an emptied previously-stored key is kept so the
        // operator can clear a wrong value.
        const stored = conversation.interaction_params || {};
        const cleanedParams = {};
        Object.entries(editedParams).forEach(([key, value]) => {
          const isEmpty = value === '' || value === null || value === undefined;
          if (isEmpty && !(key in stored)) return;
          cleanedParams[key] = value;
        });
        // An empty set means "nothing to change", never "wipe everything".
        if (Object.keys(cleanedParams).length > 0) {
          payload.interaction_params = cleanedParams;
        }
      }
      await axios.put(
        `/api/logistics/carrier-conversations/${conversation._id}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setEditingIndex(null);
      // Trigger a refresh of the conversation data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to update carriers conversation:', error);
      const detail = error.response?.data?.detail;
      setSaveError(typeof detail === 'string' && detail
        ? detail
        : t('ticketWorkspaceUi.carriers.saveFailed', 'Les modifications n’ont pas pu être enregistrées.'));
    }
  };

  // KI-205: the operator's message goes to the carrier on this conversation's
  // own channel (its e-mail thread, or the portal follow-up) and is sent at
  // once — writing it is the validation. The backend says when a reply is
  // possible (conversation.reply) and answers why not otherwise.
  const toggleReplyFile = (name) => {
    setReplyFiles((current) => (current.includes(name)
      ? current.filter((item) => item !== name)
      : [...current, name]));
  };

  const handleSendReply = async (event) => {
    event.preventDefault();
    const message = replyText.trim();
    if (!message || replySending) return;
    setReplySending(true);
    setReplyError('');
    setReplyNotice('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `/api/logistics/carrier-conversations/${conversation._id}/reply`,
        { message, files: replyFiles },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReplyText('');
      setReplyFiles([]);
      setReplyNotice(response.data?.status === 'queued'
        ? t('carriersConversation.replyQueued', 'Follow-up queued: the carrier portal is processing it (up to 2 minutes).')
        : t('carriersConversation.replySent', 'Message sent to the carrier.'));
      if (onRefresh) onRefresh();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setReplyError(typeof detail === 'string' && detail
        ? detail
        : t('carriersConversation.replyFailed', 'The message could not be sent.'));
      // A race or a failed send changed the conversation: show it as it is now.
      if (onRefresh && [409, 502].includes(error?.response?.status)) onRefresh();
    } finally {
      setReplySending(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedParams(conversation.interaction_params || {});
    setEditedMessage('');
    setParamSchema(null);
    setSaveError('');
  };

  const parseCarrierResponse = (content) => {
    if (!content) return { details: '' };

    // Split the content by email separator pattern (line starting with '>')
    const lines = content.split('\n');

    // Get the actual response (everything before the first email quote)
    const response = lines
      .filter(line => !line.trim().startsWith('>'))
      .join('\n')
      .trim();

    return {
      details: response || content // Fallback to full content if parsing fails
    };
  };

  // Get parameters to display based on carrier type
  const getDisplayParams = () => {
    if (conversation.carrier_name === 'FedEx' && (conversation.claim_payload || conversation.advanced_context)) {
      return extractFedExParams(conversation);
    }
    return conversation.interaction_params || {};
  };

  const displayParams = getDisplayParams();

  // Maps snake_case param keys to their existing translation keys.
  const PARAM_LABEL_KEYS = {
    claim_type: 'claimType',
    first_name: 'firstName',
    last_name: 'lastName',
    email: 'email',
    phone_number: 'phoneNumber',
    company_name: 'companyName',
    address_line1: 'addressLine1',
    address_line2: 'addressLine2',
    city: 'city',
    postal_code: 'postalCode',
    tracking_numbers: 'trackingNumbers',
    total_packages_delivered: 'totalPackagesDelivered',
    damaged_packages_count: 'damagedPackagesCount',
    outer_carton_damage_description: 'outerCartonDamageDescription',
    content_damage_description: 'contentDamageDescription',
    damaged_items_count: 'damagedItemsCount',
    declared_value_goods: 'declaredValueGoods',
    your_message: 'yourMessage',
    packages_sent_count: 'packagesSentCount',
    packages_missing_count: 'packagesMissingCount',
    packaging_description: 'packagingDescription',
    packaging_markings: 'packagingMarkings',
    missing_items_description: 'missingItemsDescription',
    part_serial_numbers: 'partSerialNumbers',
    electronics_serial_imei: 'electronicsSerialImei',
    order_numbers: 'orderNumbers',
    manufacturer_brand_name: 'manufacturerBrandName',
    shipment_value: 'shipmentValue',
    inner_packaging_description: 'innerPackagingDescription',
    content_damage_same_side_as_outer: 'contentDamageSameSideAsOuter',
    option_text: 'option',
    tracking_id: 'trackingId',
    price: 'price',
    file_path: 'filePath',
    motif_text: 'motif',
    motif: 'motif',
    tracking_number: 'trackingNumber',
    comments: 'comments',
    colis_content: 'colisContent',
    colis_value: 'colisValue',
    package_value: 'packageValue',
    objet: 'objet',
    package_content: 'packageContent',
    product_family: 'productFamily',
    comment: 'comment',
    shop_name: 'shopName',
    recipient_phone: 'recipientPhone',
    file_type: 'fileType',
    value: 'colisValue'
  };

  const formatParamLabel = (key) => {
    const fallback = key.replace(/_/g, ' ');
    const labelKey = PARAM_LABEL_KEYS[key];
    return labelKey ? t(`carriersConversation.${labelKey}`, fallback) : fallback;
  };

  // Deduplicate what the rest of the view already shows: the tracking number
  // lives in the header above, and message-like params are usually the
  // verbatim content of the first sent message.
  const TRACKING_PARAM_KEYS = ['tracking_number', 'tracking_id', 'tracking_numbers'];
  const MESSAGE_LIKE_PARAM_KEYS = ['message', 'comment', 'comments', 'your_message'];
  const messageContents = new Set(
    messages.map((m) => String(m.content || '').trim()).filter(Boolean)
  );

  const summaryParams = Object.entries(displayParams).filter(([key, value]) => {
    if (INTERNAL_PARAM_KEYS.has(key)) return false;
    if (value === null || value === undefined || String(value).trim() === '') return false;
    if (key === 'currency') return false; // rendered alongside declared_value_goods
    if (TRACKING_PARAM_KEYS.includes(key) && conversation.tracking_id) return false;
    if (MESSAGE_LIKE_PARAM_KEYS.includes(key) && messageContents.has(String(value).trim())) return false;
    return true;
  });

  const statusLabel = conversation.status
    ? t(`carriersConversation.status.${conversation.status}`, getStatusLabel(t, conversation.status))
    : '';
  const statusTone = STATUS_TONES[conversation.status];

  const renderParamsEditor = () => (
    <div className="tkc-params tkc-params--edit">
      {Object.entries(editedParams).map(([key, value]) => {
        const fieldSpec = (paramSchema?.fields || []).find((f) => f.key === key);
        const inputType = fieldSpec?.type || 'text';
        const fieldId = `tkc-param-${conversation._id}-${key}`;
        const handleChange = (e) =>
          setEditedParams({ ...editedParams, [key]: e.target.value });
        return (
          <div key={key} className={`u-field param-item${inputType === 'textarea' ? ' is-wide' : ''}`}>
            <label className="u-label param-label" htmlFor={fieldId}>
              {formatParamLabel(key)}
              {fieldSpec?.required && <span className="param-required" aria-hidden="true"> *</span>}
            </label>
            {inputType === 'textarea' ? (
              <textarea
                id={fieldId}
                value={value ?? ''}
                onChange={handleChange}
                className="u-textarea param-input"
                rows={3}
              />
            ) : inputType === 'select' ? (
              <select id={fieldId} value={value ?? ''} onChange={handleChange} className="u-select param-input">
                <option value="">{t('carriersConversation.selectPlaceholder', '— Choisir —')}</option>
                {(fieldSpec.options || []).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
                {value && !(fieldSpec.options || []).includes(value) && (
                  <option value={value}>{value}</option>
                )}
              </select>
            ) : (
              <input
                id={fieldId}
                type={inputType === 'number' ? 'text' : inputType}
                inputMode={inputType === 'number' ? 'decimal' : undefined}
                value={value ?? ''}
                onChange={handleChange}
                className="u-input param-input"
              />
            )}
            {fieldSpec?.help && <p className="u-help">{fieldSpec.help}</p>}
          </div>
        );
      })}
    </div>
  );

  const renderError = () => {
    const diagnosis = getCarrierFailureTexts(
      t,
      conversation.failure_diagnosis?.category,
      conversation.carrier_name,
      conversation.failure_diagnosis?.details
    );
    const retry = conversation.retry_state;
    const retryPending = Boolean(retry?.next_retry_at);
    // Autonomous correction of a parameter refusal: what the agent is doing,
    // and the options the portal offers for THIS parcel.
    const correction = conversation.correction;
    const details = conversation.failure_diagnosis?.details || {};
    const avail = details.available_parameters;
    const refusedField = Array.isArray(avail?.fields)
      ? avail.fields.find((f) => f.refused && Array.isArray(f.options)) : null;
    const inProgress = correction && ['requested', 'applied'].includes(correction.state);
    const gaveUp = correction && ['gave_up', 'timed_out'].includes(correction.state);
    let lastOutboundIndex = -1;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.direction !== 'received') { lastOutboundIndex = i; break; }
    }
    return (
      <div className="u-banner u-banner--danger carriers-conversation-error tkc-alert" role="alert">
        <CircleAlert strokeWidth={2} aria-hidden="true" />
        <div className="u-banner__body tkc-alert__body">
          <div className="u-banner__title">{diagnosis.title}</div>
          <p className="tkc-alert__line">{diagnosis.explanation}</p>
          {retryPending ? (
            <p className="tkc-alert__line tkc-alert__line--calm">
              {t('carrierFailure.retryScheduled',
                'Automatic retry at {{time}} (attempt {{attempt}} of {{max}}) — no action needed.', {
                  time: formatShortDateTime(retry.next_retry_at),
                  attempt: (retry.attempts || 0) + 1,
                  max: retry.max_attempts || 3
                })}
            </p>
          ) : (
            <p className="tkc-alert__line tkc-alert__line--strong">
              {retry?.exhausted
                ? `${t('carrierFailure.retriesExhausted', 'Automatic retries exhausted ({{max}}/{{max}}).', { max: retry.max_attempts || 3 })} ${diagnosis.exhaustedAction}`
                : diagnosis.action}
            </p>
          )}
          {diagnosis.settingsPath && (
            // Where the operator fixes it (the client ↔ contract mapping of
            // the carrier's portal accounts); then « Ré-approuver » below.
            <a className="u-link tkc-alert__link" href={diagnosis.settingsPath}>
              {diagnosis.settingsLink}
            </a>
          )}
          {inProgress && (
            <p className="tkc-alert__line tkc-alert__line--calm">
              {t('carrierFailure.correctionInProgress',
                'The AI agent is correcting the parameters automatically (attempt {{attempt}} of {{max}}) — no action needed.', {
                  attempt: (correction.attempts || 0) + (correction.state === 'requested' ? 1 : 0),
                  max: correction.max_attempts || 2
                })}
            </p>
          )}
          {gaveUp && (
            <p className="tkc-alert__line tkc-alert__line--strong">
              {t('carrierFailure.correctionGaveUp', 'The AI agent could not find valid parameters ({{attempts}} attempt(s)).', {
                attempts: correction.attempts || 0
              })}
            </p>
          )}
          {details.agent_rationale && (
            <p className="tkc-alert__line">
              {t('carrierFailure.agentRationale', 'Agent’s reason')}: {details.agent_rationale}
            </p>
          )}
          {refusedField && refusedField.options.length > 0 && (
            <p className="tkc-alert__line">
              {t('carrierFailure.availableOptions', 'Options offered by the portal for {{field}}', {
                field: refusedField.label || refusedField.name
              })}: {refusedField.options.join(' | ')}
            </p>
          )}
          {Array.isArray(conversation.failure_diagnosis?.details?.suggestions)
            && conversation.failure_diagnosis.details.suggestions.length > 0 && (
            <p className="tkc-alert__line">
              {t('carrierFailure.suggestions', 'Portal accounts with a similar name')}:{' '}
              {conversation.failure_diagnosis.details.suggestions.join(', ')}
            </p>
          )}
          <details className="tkc-alert__tech">
            <summary>{t('carrierFailure.technicalDetail', 'Technical detail')}</summary>
            <p>{contactError}</p>
          </details>
          {!retryPending && editingIndex === null && (
            // A failed conversation is recoverable from here: fix the
            // parameters and/or explicitly relaunch — re-approval resets
            // the retry ladder server-side.
            <div className="tkc-alert__actions">
              {lastOutboundIndex >= 0 && (
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => handleEditClick(lastOutboundIndex)}>
                  <Pencil strokeWidth={1.8} aria-hidden="true" />
                  {t('carriersConversation.fixParams', 'Corriger les paramètres')}
                </button>
              )}
              <button
                type="button"
                className="u-btn u-btn--primary u-btn--sm"
                onClick={() => onValidateMessage(conversation._id, true)}
                disabled={isValidating}
              >
                {t('carriersConversation.reapprove', 'Ré-approuver')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    // Approve/Reject (and Edit) only make sense on the draft awaiting
    // validation: the message must itself be pending. The conversation
    // status alone is not enough — a pending draft later in the thread
    // puts the conversation in 'pending_validation' and used to render
    // the buttons on already-sent messages too. Messages without a
    // status predate per-message statuses; for those the conversation
    // status is the only signal we have.
    // KI-108: a message flag can stay « pending » after the send
    // (35k such messages), and cancelled contacts keep their drafts:
    // only a conversation still awaiting validation offers Approve.
    const isDraftMessage = (message) =>
      editableStatuses.includes(conversation.status) &&
      (message.status === 'pending' || !message.status) &&
      message.direction !== 'received' &&
      message.direction !== 'final_response';
    // KI-195: one decision per draft — the latest one. A decision
    // sends or refuses the latest pending draft; older drafts that
    // were never sent keep their « En attente » label without
    // buttons (an Approve there would decide another message).
    let awaitingIndex = -1;
    messages.forEach((message, index) => {
      if (isDraftMessage(message)) awaitingIndex = index;
    });
    const isMessageAwaitingValidation = (message, index) =>
      index === awaitingIndex && isDraftMessage(message);

    // Only the most recent message matters day to day; older ones are
    // collapsed behind a history toggle. A draft awaiting validation or
    // a message being edited must always stay visible.
    const lastIndex = messages.length - 1;
    const historyEntries = [];
    const visibleEntries = [];
    messages.forEach((message, index) => {
      const mustStayVisible =
        index === lastIndex ||
        isMessageAwaitingValidation(message, index) ||
        editingIndex === index;
      (mustStayVisible ? visibleEntries : historyEntries).push([message, index]);
    });

    const renderMessage = (message, index) => {
      const isAwaitingValidation = isMessageAwaitingValidation(message, index);
      const isReceived = RECEIVED_DIRECTIONS.includes(message.direction);
      const isSystem = message.direction === 'system';
      const messageStatus = message.status || conversation.status;
      // The latest message's state is the conversation's, shown in the header.
      const showStatus = !isSystem && (isReceived || !(index === lastIndex && messageStatus === conversation.status));
      const SenderIcon = isReceived ? Truck : isSystem ? Sparkles : UserRound;

      return (
        <article
          key={index}
          className={`carriers-conversation-message tkc-msg tkc-msg--${message.direction}${index === lastIndex ? ' tkc-msg--latest' : ''}${isAwaitingValidation ? ' tkc-msg--awaiting' : ''}`}
        >
          <header className="tkc-msg__head">
            <span className="tkc-msg__who">
              <SenderIcon strokeWidth={1.8} aria-hidden="true" />
              {isReceived
                ? t('carriersConversation.carrier')
                : isSystem
                  ? t('carriersConversation.agent', 'AI agent')
                  : t('ticketWorkspaceUi.carriers.us', 'Nous')}
              {message.origin === OPERATOR_REPLY_ORIGIN && (
                <span className="u-badge tkc-msg__badge">{t('carriersConversation.replyAuthor', 'Written by the team')}</span>
              )}
              {message.is_no_response_recontact && (
                <span className="u-badge u-badge--accent tkc-msg__badge">{t('carriersConversation.autoRecontact', 'Automatic follow-up')}</span>
              )}
              {message.status === 'bounced' && (
                <span
                  className="u-badge u-badge--danger tkc-msg__badge"
                  title={message.bounce?.address ? `${message.bounce.address}${message.bounce.code ? ` (${message.bounce.code})` : ''}` : undefined}
                >
                  {t('carriersConversation.bounced', 'Not delivered')}
                </span>
              )}
            </span>
            <span className="tkc-msg__time">{formatTimestamp(message.timestamp)}</span>
          </header>
          <div className="tkc-msg__body">
            {editingIndex === index ? (
              <textarea
                value={editedMessage}
                onChange={(e) => setEditedMessage(e.target.value)}
                className="u-textarea message-edit-textarea"
                aria-label={t('ticketWorkspaceUi.carriers.editMessage', 'Message au transporteur')}
                rows={6}
              />
            ) : (
              <div className="carrier-response">
                {isReceived ? (() => {
                  const translation = translations[index];
                  const translatedText = translation && !translation.same_language ? translation.text : null;
                  const displayOriginal = !translatedText || showOriginal[index];
                  return (
                    <div className="carrier-response__details">
                      <div className="carrier-response__details-content">
                        <LinkifiedText text={displayOriginal ? parseCarrierResponse(message.content).details : translatedText} />
                      </div>
                      {translatedText ? (
                        <button
                          type="button"
                          className="carrier-translation-toggle tkc-msg__translate"
                          onClick={() => setShowOriginal((prev) => ({ ...prev, [index]: !prev[index] }))}
                        >
                          <Languages strokeWidth={1.8} aria-hidden="true" />
                          {showOriginal[index]
                            ? t('carriersConversation.showTranslation', 'Show the translation')
                            : t('carriersConversation.translatedFrom', 'Translated from {{language}} · show the original', {
                                language: languageName(translation.source_language, uiLanguage)
                              })}
                        </button>
                      ) : (!translation && translationState === 'loading' && (
                        <span className="carrier-translation-status tkc-msg__translate">
                          <Languages strokeWidth={1.8} aria-hidden="true" /> {t('carriersConversation.translating', 'Translating…')}
                        </span>
                      ))}
                    </div>
                  );
                })() : (
                  <div className="carrier-response__details-content">
                    <LinkifiedText text={message.content} />
                  </div>
                )}
              </div>
            )}
          </div>
          {showStatus && (
            <footer className="tkc-msg__foot">
              {/* A carrier message is received, whatever « status » it
                  carries: that field holds the conversation's state
                  when it arrived (processing, recorded…), not a
                  delivery state — « Envoi en cours » on a reply
                  confused operators (LogVAD, 2026-09-30). */}
              <span className="carriers-conversation-message__status">
                {isReceived
                  ? t('carriersConversation.status.received')
                  : t(`carriersConversation.status.${messageStatus}`)}
              </span>
            </footer>
          )}
          {editingIndex === index && (
            <div className="tkc-msg__decision">
              {saveError && <p className="u-error-text tkc-msg__error" role="alert">{saveError}</p>}
              <div className="tkc-msg__buttons">
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={handleCancelEdit}>
                  {t('carriersConversation.cancel')}
                </button>
                <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={handleSaveEdit}>
                  <Check strokeWidth={2} aria-hidden="true" />
                  {t('carriersConversation.save')}
                </button>
              </div>
            </div>
          )}
          {isAwaitingValidation && editingIndex === null && (
            <div className="carriers-conversation-message__validation tkc-msg__decision">
              {message.requires_operator === true && (
                // KI-195: a draft left « En attente » before the fix —
                // only this click sends it, whatever its age.
                <span className="carriers-conversation-message__operator-only tkc-msg__note" role="note">
                  {t('carriersConversation.operatorOnlyDraft',
                    'Brouillon en attente depuis le {{date}} — envoyé uniquement si vous l’approuvez', {
                      date: formatShortDate(message.drafted_at || message.timestamp)
                    })}
                </span>
              )}
              {isValidating && (
                <span className="carriers-conversation-message__sending tkc-msg__note" role="status">
                  {t('carriersConversation.sendingToCarrier',
                    'Envoi au transporteur en cours… (jusqu’à 2 minutes pour un formulaire en ligne)')}
                </span>
              )}
              <div className="tkc-msg__buttons">
                <button type="button" className="u-btn u-btn--ghost u-btn--sm tkc-msg__edit" onClick={() => handleEditClick(index)}>
                  <Pencil strokeWidth={1.8} aria-hidden="true" />
                  {t('carriersConversation.edit')}
                </button>
                <button
                  type="button"
                  className="u-btn u-btn--secondary u-btn--sm carriers-conversation-message__button--reject"
                  onClick={() => onValidateMessage(conversation._id, false)}
                  disabled={isValidating}
                >
                  {t('carriersConversation.reject')}
                </button>
                <button
                  type="button"
                  className="u-btn u-btn--primary u-btn--sm carriers-conversation-message__button--approve"
                  onClick={() => onValidateMessage(conversation._id, true)}
                  disabled={isValidating}
                >
                  {t('carriersConversation.approve')}
                </button>
              </div>
            </div>
          )}
        </article>
      );
    };

    return (
      <div className="carriers-conversation-messages tkc-thread">
        {historyEntries.length > 0 && (
          <>
            <button
              type="button"
              className="carriers-history-toggle tkc-toggle"
              onClick={() => setShowHistory((prev) => !prev)}
              aria-expanded={showHistory}
            >
              <History strokeWidth={1.8} aria-hidden="true" />
              {showHistory
                ? t('carriersConversation.hideHistory', "Masquer l'historique")
                : t('carriersConversation.showHistory', "Afficher l'historique ({{count}})", {
                    count: historyEntries.length
                  })}
            </button>
            {showHistory && (
              <div className="carriers-history tkc-thread__history">
                {historyEntries.map(([message, index]) => renderMessage(message, index))}
              </div>
            )}
          </>
        )}
        {visibleEntries.map(([message, index]) => renderMessage(message, index))}
      </div>
    );
  };

  const renderReply = () => {
    const reply = conversation.reply;
    const files = (ticketFiles || []).filter((file) => file?.name);
    return (
      <section
        className="carriers-conversation-reply tkc-reply"
        aria-label={t('carriersConversation.replyTitle', 'Reply to the carrier')}
      >
        {replyNotice && (
          <div className="u-banner u-banner--success tkc-reply__banner" role="status">
            <Check strokeWidth={2} aria-hidden="true" />
            <div className="u-banner__body">{replyNotice}</div>
          </div>
        )}
        {replyError && (
          <div className="u-banner u-banner--danger tkc-reply__banner" role="alert">
            <CircleAlert strokeWidth={2} aria-hidden="true" />
            <div className="u-banner__body">{replyError}</div>
          </div>
        )}
        {reply.available ? (
          <form className="tkc-reply__form" onSubmit={handleSendReply}>
            <div className="tkc-reply__head">
              <Reply strokeWidth={1.8} aria-hidden="true" />
              <strong>{t('carriersConversation.replyTitle', 'Reply to the carrier')}</strong>
            </div>
            <span className="tkc-reply__channel">
              {reply.channel === 'portal'
                ? t('carriersConversation.replyToPortal', 'On the {{carrier}} portal, as a follow-up to this request', {
                    carrier: conversation.carrier_name || ''
                  })
                : t('carriersConversation.replyToEmail', "By e-mail to {{address}}, in this request's thread", {
                    address: reply.to || ''
                  })}
            </span>
            <textarea
              className="u-textarea tkc-reply__input"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              maxLength={REPLY_MAX_CHARS}
              placeholder={t('carriersConversation.replyPlaceholder', 'Write the information the carrier asked for…')}
              aria-label={t('carriersConversation.replyTitle', 'Reply to the carrier')}
              disabled={replySending}
              rows={4}
            />
            {files.length > 0 && (
              <fieldset className="tkc-reply__files">
                <legend>{t('carriersConversation.replyAttach', 'Attach ticket files')}</legend>
                {files.map((file) => {
                  const selected = replyFiles.includes(file.name);
                  return (
                    <label key={file.name} className={`tkc-reply__file${selected ? ' is-selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleReplyFile(file.name)}
                        disabled={replySending}
                      />
                      <Paperclip strokeWidth={1.8} aria-hidden="true" />
                      <span>{file.name}</span>
                    </label>
                  );
                })}
              </fieldset>
            )}
            <div className="tkc-reply__actions">
              <span className="tkc-reply__count">
                {replyText.length} / {REPLY_MAX_CHARS}
              </span>
              <button type="submit" className="u-btn u-btn--primary u-btn--sm" disabled={!replyText.trim() || replySending}>
                <Send strokeWidth={1.8} aria-hidden="true" />
                {replySending
                  ? t('carriersConversation.replySending', 'Sending…')
                  : t('carriersConversation.replySend', 'Send to the carrier')}
              </button>
            </div>
          </form>
        ) : (reply.reason && (
          <p className="tkc-reply__unavailable">
            <Info strokeWidth={1.8} aria-hidden="true" />
            <span>{reply.reason}</span>
          </p>
        ))}
      </section>
    );
  };

  const isEditing = editingIndex !== null;

  return (
    <div className="carriers-conversation tkc">
      {(conversation.carrier_name || conversation.tracking_id || statusLabel) && (
        <div className="tkc-head">
          <div className="tkc-head__id">
            {conversation.carrier_name && <strong className="tkc-head__carrier">{conversation.carrier_name}</strong>}
            {conversation.tracking_id && (
              <code className="u-code" title={t('carriersConversation.trackingId')}>{conversation.tracking_id}</code>
            )}
          </div>
          {statusLabel && (
            <span className={`u-badge${statusTone ? ` u-badge--${statusTone}` : ''}`}>
              <span className="u-badge__dot" aria-hidden="true" />
              {statusLabel}
            </span>
          )}
        </div>
      )}

      {!contactError && conversation.fallback_contact_method === 'email' && (
        // The portal could not take the claim (refused / parcel not visible on
        // the account) and the executor sent it to the carrier's contact
        // address instead: the operator must know the portal follow-up does
        // not apply and why (errors_07_09.md §7 S4).
        <div className="u-banner carriers-conversation-notice" role="status">
          <Info strokeWidth={2} aria-hidden="true" />
          <div className="u-banner__body">
            <div className="u-banner__title">{t('carrierFallback.emailTitle', 'Claim sent by e-mail to the carrier')}</div>
            {t('carrierFallback.emailExplanation',
              'The carrier portal could not take this claim online, so it was sent to the carrier\'s contact address. The portal will not show its progress; check the reason below (tracking number, account) before relying on it.')}
            {conversation.fallback_reason && (
              <div className="tkc-alert__line tkc-alert__line--calm">
                {t('carrierFailure.technicalDetail', 'Technical detail')}: {conversation.fallback_reason}
              </div>
            )}
          </div>
        </div>
      )}

      {contactError && renderError()}

      {(isEditing ? Object.keys(editedParams).length > 0 : summaryParams.length > 0) && (
        isEditing ? (
          <section className="tkc-panel" aria-label={t('ticketWorkspaceUi.carriers.params', 'Paramètres de la demande')}>
            <div className="tkc-panel__title">{t('ticketWorkspaceUi.carriers.editParams', 'Modifier la demande')}</div>
            {renderParamsEditor()}
          </section>
        ) : (
          <section className="tkc-panel">
            <button
              type="button"
              className="carriers-params-toggle tkc-panel__toggle"
              onClick={() => setShowParams((prev) => !prev)}
              aria-expanded={showParams}
            >
              <ChevronDown className="tkc-panel__chevron" strokeWidth={2} aria-hidden="true" />
              {showParams
                ? t('carriersConversation.hideParams', 'Masquer les paramètres')
                : t('carriersConversation.showParams', 'Paramètres de la demande ({{count}})', {
                    count: summaryParams.length
                  })}
            </button>
            {showParams && (
              <dl className="tkc-params carriers-conversation-params">
                {summaryParams.map(([key, value]) => {
                  const text = key === 'declared_value_goods' && displayParams.currency
                    ? `${value} ${displayParams.currency}`
                    : String(value);
                  return (
                    <div key={key} className={`param-item${text.length > 32 ? ' is-wide' : ''}`}>
                      <dt className="param-label">{formatParamLabel(key)}</dt>
                      <dd className="param-value">{text}</dd>
                    </div>
                  );
                })}
              </dl>
            )}
          </section>
        )
      )}

      {conversation.status !== 'deleted' && renderMessages()}

      {conversation.reply && editingIndex === null && renderReply()}
    </div>
  );
};

export default CarriersConversation;
